import { skipToken } from '@apollo/client/react'
import { useAtomValue } from 'jotai'
import { kebabCase } from 'lodash-es'
import {
  type ContextType,
  type PropsWithChildren,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { type PressEvent } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { tv } from 'tailwind-variants'
import { match } from 'ts-pattern'
import type { Promisable } from 'type-fest'
import { joinURL, withQuery } from 'ufo'
import FluentAdd24Filled from '~icons/fluent/add-24-filled'
import FluentArrowDownload20Filled from '~icons/fluent/arrow-download-20-filled'
import FluentArrowDownloadOff20Filled from '~icons/fluent/arrow-download-off-20-filled'
import FluentChevronRight20Regular from '~icons/fluent/chevron-right-20-regular'
import FluentGlobe20Regular from '~icons/fluent/globe-20-regular'
import FluentGlobeOff20Regular from '~icons/fluent/globe-off-20-regular'
import FluentImage20Regular from '~icons/fluent/image-20-regular'
import FluentImageArrowForward20Regular from '~icons/fluent/image-arrow-forward-20-regular'
import FluentImageOff20Regular from '~icons/fluent/image-off-20-regular'
import FluentMoney20Regular from '~icons/fluent/money-20-regular'
import FluentMoneyOff20Regular from '~icons/fluent/money-off-20-regular'
import FluentPersonStarburst20Regular from '~icons/fluent/person-starburst-20-regular'
import FluentThumbLike16Filled from '~icons/fluent/thumb-like-16-filled'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import IcBaselineCheck from '~icons/ic/baseline-check'
import IcBaselineClose from '~icons/ic/baseline-close'
import IcOutlineBrush from '~icons/ic/outline-brush'
import { DEFAULT_LORA_MODEL_WEIGHT } from 'src/components/GenerationTask/constants'
import { useAvaliableI2vModels } from 'src/components/GenerationTask/MultiColumnGenerationForm/ImageToVideo/helper'
import { encodeInitialValuesForImport } from 'src/components/GenerationTask/MultiColumnGenerationForm/importEncode'
import Rating, { RatingInput } from 'src/components/Rating'
import { UIButton } from 'src/components/ui/Button'
import { UILink } from 'src/components/ui/Link'
import Text from 'src/components/ui/Text'
import UserLink from 'src/components/UserWidget/UserLink'
import { useMyInfo } from 'src/services/apollo/auth'
import {
  type GenerationModelDetailFragment,
  GenerationModelMarkType,
  type GenerationModelVersionDetailFragment,
  ReviewRating,
} from 'src/services/apollo/generated/graphql'
import {
  executeMarkGenerationModel,
  useListGenerationModelVersions,
} from 'src/services/apollo/generation-model'
import { parseRatingDetail } from 'src/services/apollo/review'
import {
  executeSetFollowState,
  useGetUserInfoById,
} from 'src/services/apollo/user'
import { localeAtom } from 'src/stores/locale'
import { cx } from 'src/utils/cx'
import { formatNum } from 'src/utils/formatNum'
import {
  isModelPreviewTest,
  isModelPrivate,
  parseModelPermissions,
} from 'src/utils/generationModel/modelPermissions'
import { getNodesFromConnection } from 'src/utils/graphql'
import { getThumbnailUrl } from 'src/utils/media'
import {
  isChatModel,
  isDitV1Model,
  isLora,
  isMMDIT26AModel,
  isMultiUnionLora,
  isSDModel,
  isUserLora,
  isVideoModel,
  normalizeModelType,
} from 'src/utils/model'
import { startEventGroup, track } from 'src/utils/tracker'
import { getUserDisplayName } from 'src/utils/user'
import { openSignModal } from '../../Account/utils'
import { modelPermissions } from '../constants'
import { ModelDetailContext } from '../context'
import { openModelPreviewTestIneligibleDialog } from '../ModelPermissionDialog'
import { GenerationModelVersionStatus } from '../ModelTypeChip'
import styles from './ModelInfoSection.module.css'
import ModelParamsInfo from './ModelParametersInfo'
import ModelStyleKeyDisplay from './ModelStyleKeyDisplay'

const fieldsToCheck = [
  'samplingMethod',
  'samplingSteps',
  'cfgScale',
  'negativePrompts',
]
function getVersionExtraParams(version: GenerationModelVersionDetailFragment) {
  const { extra } = version
  return Object.fromEntries(
    Object.entries(extra).filter(
      ([key, value]) => fieldsToCheck.includes(key) && value != null,
    ),
  )
}

function formatUploadedAtByLocale(
  value: string | undefined,
  locale: string,
): string | undefined {
  if (!value) return undefined
  const normalizedLocale = locale === 'zh' ? 'zh-TW' : locale
  return new Intl.DateTimeFormat(normalizedLocale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

export default function ModelInfoSection(props: {
  model: GenerationModelDetailFragment
  version?: GenerationModelVersionDetailFragment
  isSinglePage?: boolean
  trainBaseModelId?: string
}) {
  const {
    model,
    version = model?.latestAvailableVersion,
    loadingVersion,
    onUse,
  }: ContextType<typeof ModelDetailContext> = useContext(ModelDetailContext)
  const locale = useAtomValue(localeAtom)

  if (!model) return null

  const isMMDIT26A = isMMDIT26AModel(model.type)

  return (
    <div className={cx('flex-1 flex-shrink min-w-0 flex flex-col gap-3')}>
      <div className={cx(onUse && 'max-mmd:hidden')}>
        <ModelContentActions model={model} version={version} />
      </div>
      <AuthorInfo model={model} />
      {isMMDIT26A && (
        <ModelStyleKeyDisplay
          version={version}
          modelType={normalizeModelType(model.type)}
        />
      )}
      <dl className={cx(styles['info-table'], loadingVersion && 'opacity-50')}>
        {!isMMDIT26A && (
          <div>
            <dt>
              <Text i18nKey="market:model.uploaded-at.label" />
            </dt>
            <dd>{formatUploadedAtByLocale(version?.createdAt, locale)}</dd>
          </div>
        )}
        {/* Hide usage info for private models */}
        {!isModelPrivate(model) && (
          <>
            <div>
              <dt>
                <Text i18nKey="market:model.lora-used-times" />
              </dt>
              <dd>{formatNum(model.refCount) ?? 0}</dd>
            </div>
            {isUserLora(model.type) && model.rebateAmount != null && (
              <div>
                <dt>
                  <Text i18nKey="market:train-lora.model-rebate.earned" />
                </dt>
                <dd className="flex items-center gap-1 text-theme-primary">
                  <GameIconsCrystalBars className="size-[1em]" />
                  {model.rebateAmount.toLocaleString()}
                </dd>
              </div>
            )}
            {model.reviewSummary && <RatingPartial />}
          </>
        )}
        <ModelParamsInfo
          contentClassName="pl-2"
          version={version}
          modelType={model.type}
          trainBaseModelId={props.trainBaseModelId}
        />
        <PermissionInfo />
      </dl>
    </div>
  )
}

function ModelContentActions(props: {
  model: GenerationModelDetailFragment
  version?: GenerationModelVersionDetailFragment
}) {
  const { t } = useTranslation([
    'market',
    'user',
    'common',
    'action',
    'artwork',
  ])

  const { onClose, onUse } = useContext(ModelDetailContext)
  const { data: userInfo } = useMyInfo()
  const navigate = useNavigate()
  const { model, version } = props

  const loraIsCompositeUnion = isMultiUnionLora(model.type)
  const { data: compositeLoraVersions } = useListGenerationModelVersions(
    loraIsCompositeUnion ? { modelId: model.id, last: 1 } : skipToken,
  )

  const currentVersion =
    version ??
    (isMultiUnionLora(model.type)
      ? getNodesFromConnection(compositeLoraVersions)?.[0]
      : model.latestAvailableVersion)
  const currentVersionId = currentVersion?.id

  const { models: i2vModelsDynamicList } = useAvaliableI2vModels()

  const isI2vLora = useMemo(() => {
    for (const i2vModel of i2vModelsDynamicList) {
      if (i2vModel.loras?.find(lora => lora.id === currentVersionId)) {
        return true
      }
    }
    return false
  }, [i2vModelsDynamicList, model])

  let label: string
  let onClick: ((ev: PressEvent) => Promisable<void>) | undefined

  const canBookmarkModel = !isVideoModel(model.type)
  const eligibleToGen = parseModelPermissions(model.userPermission).canGenerate

  if (!eligibleToGen && isModelPreviewTest(model)) {
    label = t('market:model.use-this-model')
    onClick = () => {
      const trackingLocation = 'model-detail-use-early-access'
      if (!userInfo?.id) {
        const oauthRedirectUrl = joinURL(
          window.location.origin,
          `/model/${model.id}`,
        )
        return openSignModal(
          { oauthRedirectUrl },
          { fromPath: trackingLocation },
        )
      }
      openModelPreviewTestIneligibleDialog()
    }
  } else if (
    isLora(model.type) ||
    isSDModel(model.type) ||
    isChatModel(model.type) ||
    isDitV1Model(model.type) ||
    isMMDIT26AModel(model.type) ||
    isVideoModel(model.type)
  ) {
    let trackEvent: string
    let fromPath: string
    let url: string

    // TODO: implement video lora logic properly when needed as current logic looks outdated
    if (isLora(model.type)) {
      trackEvent = 'use_lora_click'
      fromPath = 'use-lora'
      label = t('market:model.use-this-lora')
      if (isI2vLora) {
        url = '/generator/image/animation'
        if (currentVersionId) {
          const initialValues = encodeInitialValuesForImport({
            lora: { [currentVersionId]: 1 },
          })
          url = withQuery(url, { initialValues })
        }
      } else {
        url = '/generator/image'
        if (currentVersionId) {
          const initialValues = encodeInitialValuesForImport({
            lora: { [currentVersionId]: DEFAULT_LORA_MODEL_WEIGHT },
            loraParameters: [
              {
                versionId: currentVersionId,
                triggerWords: currentVersion.extra.triggerWords ?? '',
                weight: DEFAULT_LORA_MODEL_WEIGHT,
              },
            ],
          })
          url = withQuery(url, { initialValues })
        }
      }
    } else if (isChatModel(model.type)) {
      trackEvent = 'use_chat_click'
      fromPath = 'use_chat'
      label = t('market:model.use-this-model')
      url = '/generator/image'
      if (currentVersionId) {
        const initialValues = encodeInitialValuesForImport({
          modelId: currentVersionId,
          ...getVersionExtraParams(currentVersion!),
        })
        url = withQuery(url, { initialValues })
      }
    } else if (isVideoModel(model.type)) {
      trackEvent = 'use_video_model_click'
      fromPath = 'use_video_model'
      label = t('market:model.use-this-model')
      url = '/generator/image/animation'
      // TODO: fill in video model initial values properly
    } else {
      trackEvent = 'use_model_click'
      fromPath = 'use_model'
      label = t('market:model.use-this-model')
      url = '/generator/image'
      if (currentVersionId) {
        const initialValues = encodeInitialValuesForImport({
          modelId: currentVersionId,
          ...getVersionExtraParams(currentVersion),
        })
        url = withQuery(url, { initialValues })
      }
    }

    onClick = async () => {
      track(trackEvent, { type: model.type })
      if (!userInfo) {
        openSignModal(undefined, { fromPath })
        return
      }
      if (canBookmarkModel) {
        try {
          await executeMarkGenerationModel({
            modelId: model.id,
            target: true,
            type: GenerationModelMarkType.Bookmark,
          })
          toast.info(t('market:model.notice.bookmark-success'))
        } catch (e: any) {
          toast.error('Failed to bookmark model', e.message)
          return
        }
      }
      startEventGroup('generate_source')
      if (onUse) {
        onUse()
      } else {
        navigate(url)
      }
      onClose?.()
    }
  } else {
    label = t('market:model.model-unavailable')
  }

  const disableUseBtn =
    !currentVersion?.id ||
    currentVersion?.status !== GenerationModelVersionStatus.available

  return (
    <>
      {currentVersion?.status === 'invalid' ? (
        <div className="w-full flex items-center justify-center font-bold bg-orange-700 text-white p-2 rounded-xl">
          {t('market:model.conversion-failed')}
        </div>
      ) : (
        <div className="flex gap-x-4 gap-y-2 flex-wrap">
          <UIButton
            variant="filled"
            className="flex-1 text-sm whitespace-nowrap"
            size="large"
            icon={<IcOutlineBrush className="size-6" />}
            endIcon={<></>}
            onPress={onClick}
            disabled={disableUseBtn}
          >
            {label}
          </UIButton>
          {disableUseBtn && (
            <p className="text-sm italic text-opacity-80">
              {t('market:model.use-another-version')}
            </p>
          )}
        </div>
      )}
    </>
  )
}

function AuthorInfo(props: {
  model: GenerationModelDetailFragment
  className?: string
}) {
  const { model, className } = props
  const { t } = useTranslation(['market'])

  const { data: me } = useMyInfo()
  const partialAuthor = model.author
  const { data, refetch: reloadAuthor } = useGetUserInfoById(model.authorId)

  const author = data || partialAuthor

  if (!model.extra) return null

  const beingUserLora = isUserLora(model.type)
  const { claimed } = model.extra
  const beingMyModel = me && author?.id === me.id

  const authorToDisplay = (() => {
    if (claimed) return author
    if (beingUserLora) return author
    if (!claimed && beingMyModel) return me
  })()

  const baseStyle =
    'border border-solid border-black/15 dark:border-white/15 rounded-lg'

  if (!authorToDisplay) return null

  const displayName =
    !beingUserLora && beingMyModel
      ? t('market:model.model-by-self')
      : getUserDisplayName(authorToDisplay)

  return (
    <div className={cx(baseStyle, className)}>
      {author?.coverMedia && (
        <div
          className="aspect-[5] bg-center bg-cover rounded-t-lg"
          style={{
            backgroundImage: `url(${getThumbnailUrl(author.coverMedia)})`,
          }}
        />
      )}
      <div className="flex items-center gap-2 p-1.5 pl-2">
        <span className="text-sm whitespace-nowrap">
          {beingUserLora || claimed
            ? t('market:model.trained-by')
            : t('market:model.post-by')}
        </span>
        <UserLink
          user={authorToDisplay}
          name={displayName}
          linkClassName="min-w-0"
        />
        {!me || !author || beingMyModel || data == null ? null : (
          <UIButton
            variant={data.followedByMe ? 'outlined' : 'filled'}
            busy="auto"
            className="ml-auto whitespace-nowrap"
            onPress={async () => {
              const userId = author.id
              const target = !data.followedByMe
              track('follow_btn_click', {
                userId,
                target,
                scene: 'model',
              })
              await executeSetFollowState({
                userId,
                target,
              })
              reloadAuthor()
            }}
          >
            {data.followedByMe ? (
              <Text i18nKey="user:following" />
            ) : (
              <Text i18nKey="user:follow" />
            )}
          </UIButton>
        )}
      </div>
      {beingUserLora && !beingMyModel && (
        <div className="grid p-0.5 border-t border-black/15 dark:border-white/15">
          <UILink
            href="/train-lora"
            variant="filled"
            color="theme"
            icon={<FluentAdd24Filled className="size-6" />}
            endIcon={<></>}
            className="text-center rounded-md"
            onPress={() => {
              track('lora_train_click', {
                location: 'trained-lora',
              })
            }}
          >
            <Text i18nKey="growth:train-lora.credit-banner.title" />
          </UILink>
        </div>
      )}
    </div>
  )
}

function RatingPartial() {
  const { model } = useContext(ModelDetailContext)
  const reviewSummary = model?.reviewSummary
  const positiveCount =
    reviewSummary?.ratingDetail &&
    parseRatingDetail(reviewSummary.ratingDetail)[ReviewRating.Like]

  const { data: me } = useMyInfo()

  return (
    <>
      <div>
        <dt>
          <Text i18nKey="common:review.label" />
        </dt>
        <dd className="flex items-center flex-wrap">
          <span className="inline-flex items-center">
            <Rating value={reviewSummary?.score} />
            {Number(positiveCount) > 0 && (
              <>
                &nbsp; (
                <ReviewCountPrefix>
                  {formatNum(positiveCount)}
                </ReviewCountPrefix>
                )
              </>
            )}
          </span>
          {me && model && (
            <span className="ml-auto -my-1 flex">
              <RatingInput
                itemId={model.id}
                value={model.myReview?.rating}
                className=""
              />
            </span>
          )}
        </dd>
      </div>
    </>
  )
}

function ReviewCountPrefix({ children }: PropsWithChildren) {
  return (
    <>
      <FluentThumbLike16Filled className="mr-0.5 size-4 text-green-600" />
      {children}
    </>
  )
}

function PermissionInfo() {
  const { t } = useTranslation(['market'])
  const { model } = useContext(ModelDetailContext)
  const [open, setOpen] = useState(false)
  const detailsRef = useRef<HTMLDetailsElement>(null)

  if (!model) return null

  const items = [
    // Not actually a permission, but always displayed info
    {
      permission: 'allowGenerateUse',
      permissionLabel: (
        <Text
          i18nKey="market:model.permissions.allow-generate-use"
          defaults={'Allow users to generate and share images using this model'}
        />
      ),
      allowed: true,
    },
    // Downloadable is not a permission from model.extra
    {
      permission: 'isDownloadable',
      permissionLabel: (
        <Text
          i18nKey="market:submit.allow-download"
          defaults={t('market:model.permissions.unknown')}
        />
      ),
      allowed: model.isDownloadable,
    },
    ...modelPermissions.map(p => ({
      permission: p,
      // compatibility for old model permissions
      allowed:
        !!model.extra?.permissions?.[p] ||
        !!model.extra?.permissions?.[kebabCase(p)],
      permissionLabel: (
        <Text
          i18nKey={`market:model.permissions.${kebabCase(p)}`}
          defaults={t('market:model.permissions.unknown')}
        />
      ),
    })),
  ]

  return (
    <div
      className="cursor-pointer"
      onClick={() => {
        if (detailsRef.current) {
          detailsRef.current.open = !detailsRef.current.open
        }
      }}
    >
      <dt>
        <Text i18nKey="market:model.permissions.title" />
        <FluentChevronRight20Regular
          className={cx('ml-auto size-5', open && 'rotate-90')}
        />
      </dt>
      <dd>
        <details
          ref={detailsRef}
          className="group"
          onToggle={ev => setOpen(ev.currentTarget.open)}
        >
          <summary
            className="flex gap-1 [.group[open]_&]:hidden"
            onClick={ev => ev.stopPropagation()}
          >
            {items.map(it => (
              <PermissionIcon
                {...it}
                key={it.permission}
                className="flex-none"
              />
            ))}
          </summary>
          <ul className="space-y-0.5">
            {items.map(it => (
              <li key={it.permission} className="flex gap-1 items-start">
                <PermissionIcon {...it} className="flex-none" />
                <span className={cx(!it.allowed && 'opacity-50')}>
                  {it.permissionLabel}
                </span>
              </li>
            ))}
          </ul>
        </details>
      </dd>
    </div>
  )
}

const permissionIconStyle = tv({
  base: 'size-5',
  variants: {
    allowed: {
      true: 'text-green-600',
      false: 'text-zinc-400',
    },
  },
})

function PermissionIcon(props: {
  permission: string
  allowed: boolean
  className?: string
}) {
  const allowed = Boolean(props.allowed)
  const className = permissionIconStyle({
    allowed,
    className: props.className,
  })

  return (
    match({ permission: props.permission, allowed })
      .with({ permission: 'shareImagesOnline', allowed: true }, () => (
        <FluentGlobe20Regular className={className} />
      ))
      .with({ permission: 'shareImagesOnline', allowed: false }, () => (
        <FluentGlobeOff20Regular className={className} />
      ))
      .with({ permission: 'personalUses', allowed: true }, () => (
        <FluentImage20Regular className={className} />
      ))
      .with({ permission: 'personalUses', allowed: false }, () => (
        <FluentImageOff20Regular className={className} />
      ))
      .with({ permission: 'commercialUses', allowed: true }, () => (
        <FluentMoney20Regular className={className} />
      ))
      .with({ permission: 'commercialUses', allowed: false }, () => (
        <FluentMoneyOff20Regular className={className} />
      ))
      // I failed to find an "off" variant of this icon
      .with({ permission: 'shouldCreditAuthor' }, () => (
        <FluentPersonStarburst20Regular className={className} />
      ))
      .with({ permission: 'isDownloadable', allowed: true }, () => (
        <FluentArrowDownload20Filled className={className} />
      ))
      .with({ permission: 'isDownloadable', allowed: false }, () => (
        <FluentArrowDownloadOff20Filled className={className} />
      ))
      // This permission only has "allowed" state
      .with({ permission: 'allowGenerateUse' }, () => (
        <FluentImageArrowForward20Regular className={className} />
      ))
      .with({ allowed: true }, () => <IcBaselineCheck className={className} />)
      .with({ allowed: false }, () => <IcBaselineClose className={className} />)
      .exhaustive()
  )
}
